import axios from 'axios';
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compute the correct API base URL.
 *
 * When running on a *.woyof.com subdomain or a custom merchant domain,
 * API calls go through the SAME origin (the Cloudflare Worker proxies
 * /api/* to the backend). This avoids cross-origin issues entirely.
 *
 * When running on the preview/dev environment, use REACT_APP_BACKEND_URL.
 */
function resolveApiBase() {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    // Production: any *.woyof.com subdomain or the root domain
    if (h === 'woyof.com' || h === 'www.woyof.com' || h.endsWith('.woyof.com')) {
      return `${window.location.origin}/api`;
    }
    // Custom merchant domain (not localhost, not preview) — also use same-origin
    // since the Cloudflare Worker proxies all traffic including /api/*
    const isLocal = h === 'localhost' || h === '127.0.0.1';
    const isPreview = h.includes('.preview.emergentagent.com') || h.includes('.vercel.app') || h.includes('.netlify.app') || h.includes('.railway.app');
    if (!isLocal && !isPreview) {
      return `${window.location.origin}/api`;
    }
  }
  return `${BACKEND_URL}/api`;
}

export const API = resolveApiBase();

export const api = axios.create({
  baseURL: API,
});

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDate = (date) => {
  return new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

export const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-emerald-100 text-emerald-800',
    ready: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getStatusLabel = (status) => {
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: 'Prête',
    delivered: 'Livrée',
    completed: 'Terminée',
    cancelled: 'Annulée',
    active: 'Actif',
    inactive: 'Inactif',
    unpaid: 'Non payé',
    paid: 'Payé',
    delivery: 'Livraison',
    pickup: 'À emporter',
    dine_in: 'Sur place',
  };
  return labels[status] || status;
};